/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { InfoPopover } from "components/InfoPopover"
import InputError from "components/InputError"
import { UserParameters } from "features/userParameters/UserParametersSchema"
import { useEffect } from "react"
import DatePicker from "react-datepicker"
import { Controller, useFormContext, UseFormRegister, useWatch } from "react-hook-form"
import { toDisplayDate } from "utils/dateUtils"

export interface CashFlowProps {
  index: number
  register: UseFormRegister<UserParameters>
  remove: (index?: number | number[]) => void
}

const CashFlow = ({ index, register, remove }: CashFlowProps) => {
  const {
    control,
    trigger,
    watch,
    clearErrors,
    unregister,
    formState: { errors },
  } = useFormContext<UserParameters>()

  const frequencyValue = useWatch({
    control,
    name: `cashFlows.${index}.frequency`,
  })

  useEffect(() => {
    if (frequencyValue === "one-time") {
      unregister(`cashFlows.${index}.end`)
    }
  }, [frequencyValue, unregister, index])

  const retirementStart = watch("retirementStart")
  const retirementHorizonMonths = watch("retirementHorizonMonths")

  // ensures an error is shown for the end date if the retirement length is changed
  // such that the end date follows the end of the retirement period
  useEffect(() => {
    const endValue = watch(`cashFlows.${index}.end`)
    if (endValue) {
      void trigger(`cashFlows.${index}.end`)
    }
  }, [retirementHorizonMonths, trigger, index, watch, clearErrors])

  // ensures an error is shown for the start date if the retirement start date is changed to follow it
  // or if it's changed such that the end date follows the end of the retirement period
  useEffect(() => {
    const startValue = watch(`cashFlows.${index}.start`)
    // during a form reset from a parameters file upload
    // the start value may be undefined, perhaps momentarily
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (startValue) {
      void trigger(`cashFlows.${index}.start`)
    }
    const endValue = watch(`cashFlows.${index}.end`)
    if (endValue) {
      void trigger(`cashFlows.${index}.end`)
    }
  }, [retirementStart, trigger, index, watch, clearErrors])

  return (
    <div className="cash-flow-section border-bottom" data-index={index}>
      <div className="row py-1">
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor={`flow-${index}-name`} className="col-form-label">
            Name
          </label>
          <div className="input-group">
            <input
              type="text"
              inputMode="text"
              className={`form-control${errors.cashFlows?.[index]?.name ? " is-invalid" : ""}`}
              id={`flow-${index}-name`}
              aria-invalid={!!errors.cashFlows?.[index]?.name}
              {...register(`cashFlows.${index}.name`)}
            />
            <InputError error={errors.cashFlows?.[index]?.name} />
          </div>
        </div>
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor={`flow-${index}-amount`} className="col-form-label">
            Amount
          </label>
          <div className="input-group">
            <span className="input-group-text">$</span>
            <input
              type="text"
              inputMode="decimal"
              className={`form-control${errors.cashFlows?.[index]?.amount ? " is-invalid" : ""}`}
              id={`flow-${index}-amount`}
              aria-invalid={!!errors.cashFlows?.[index]?.amount}
              {...register(`cashFlows.${index}.amount`)}
            />
            <InputError error={errors.cashFlows?.[index]?.amount} />
          </div>
        </div>
        <fieldset className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <legend className="col-form-label">Frequency</legend>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              id={`flow-${index}-annual-frequency`}
              value="annually"
              {...register(`cashFlows.${index}.frequency`)}
            />
            <label className="form-check-label" htmlFor={`flow-${index}-annual-frequency`}>
              Annually
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              id={`flow-${index}-monthly-frequency`}
              value="monthly"
              {...register(`cashFlows.${index}.frequency`)}
            />
            <label className="form-check-label" htmlFor={`flow-${index}-monthly-frequency`}>
              Monthly
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              id={`flow-${index}-one-time-frequency`}
              value="one-time"
              {...register(`cashFlows.${index}.frequency`)}
            />
            <label className="form-check-label" htmlFor={`flow-${index}-one-time-frequency`}>
              One-Time
            </label>
          </div>
        </fieldset>
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor={`flow-${index}-inflation-adjusted`} className="col-form-label">
            Inflation Adjusted
          </label>
          <div className="input-group">
            <input
              type="checkbox"
              className="form-check-input"
              id={`flow-${index}-inflation-adjusted`}
              {...register(`cashFlows.${index}.inflationAdjusted`)}
            />
          </div>
        </div>
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor={`flow-${index}-start`} className="col-form-label">
            Start
          </label>
          <Controller
            name={`cashFlows.${index}.start`}
            control={control}
            render={({ field, fieldState: { error } }) => {
              return (
                <>
                  <div className={`input-group ${error ? "is-invalid" : ""}`}>
                    <DatePicker
                      id={`flow-${index}-start`}
                      ref={elem => {
                        if (elem) {
                          field.ref(elem.input)
                        }
                      }}
                      className={`form-control ${error ? "is-invalid" : ""}`}
                      dateFormat="MM/yyyy"
                      showMonthYearPicker
                      autoComplete="off"
                      placeholderText="mm/yyyy"
                      selected={toDisplayDate(field.value)}
                      onChange={(date: Date | null) => {
                        if (date) {
                          field.onChange({
                            month: date.getMonth() + 1,
                            year: date.getFullYear(),
                          })
                        } else {
                          field.onChange(null)
                        }

                        void trigger(`cashFlows.${index}.end`)
                      }}
                      onBlur={field.onBlur}
                    />
                  </div>
                  <InputError error={error} />
                </>
              )
            }}
          />
        </div>
        {frequencyValue !== "one-time" && (
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
            <label htmlFor={`flow-${index}-end`} className="col-form-label">
              End
              <InfoPopover content="Flow includes this month." />
            </label>
            <Controller
              name={`cashFlows.${index}.end`}
              control={control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <>
                    <div className={`input-group ${error ? "is-invalid" : ""}`}>
                      <DatePicker
                        id={`flow-${index}-end`}
                        ref={elem => {
                          if (elem) {
                            field.ref(elem.input)
                          }
                        }}
                        className={`form-control ${error ? "is-invalid" : ""}`}
                        dateFormat="MM/yyyy"
                        showMonthYearPicker
                        autoComplete="off"
                        placeholderText="mm/yyyy"
                        selected={toDisplayDate(field.value ?? null)}
                        onChange={(date: Date | null) => {
                          if (date) {
                            field.onChange({
                              month: date.getMonth() + 1,
                              year: date.getFullYear(),
                            })
                          } else {
                            field.onChange(null)
                          }
                        }}
                        onBlur={field.onBlur}
                      />
                    </div>
                    <InputError error={error} />
                  </>
                )
              }}
            />
          </div>
        )}
      </div>
      <div className="row">
        <div className="col-lg-3 col-md-4 col-sm-6 col-12 py-3">
          <button
            type="button"
            className="btn btn-secondary remove-cash-flow"
            onClick={() => {
              remove(index)
            }}
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  )
}

export default CashFlow
